// Ported from the tsts script on goodcalculators.com/flesch-kincaid-calculator so scores match that calculator.

export interface ReadingLevel {
  schoolLevel: string;
  notes: string;
  id: number;
}

const TEXT_BLOCK_END_TAGS = ["li", "p", "h1", "h2", "h3", "h4", "h5", "h6", "dd"];

const WORD_NOISE_PATTERN = /[^\w' -]|[ '-]{2,}|[\b\s]['-]\s|\s['-][\b\s]|_/gm;

const READING_LEVELS: Array<ReadingLevel & { min: number; max: number }> = [
  { min: 90, max: 100, schoolLevel: "5th grade", notes: "Very easy to read", id: 1 },
  { min: 80, max: 90, schoolLevel: "6th grade", notes: "Easy to read", id: 2 },
  { min: 70, max: 80, schoolLevel: "7th grade", notes: "Fairly easy to read", id: 3 },
  { min: 60, max: 70, schoolLevel: "8th & 9th grade", notes: "Plain English", id: 4 },
  { min: 50, max: 60, schoolLevel: "10th to 12th grade", notes: "Fairly difficult to read", id: 5 },
  { min: 30, max: 50, schoolLevel: "College", notes: "Difficult to read", id: 6 },
  { min: 0, max: 30, schoolLevel: "College graduate", notes: "Very difficult to read", id: 7 },
];

const KNOWN_WORD_SYLLABLES = new Map<string, number>(
  Object.entries({
    abare: 3,
    abalone: 4,
    abed: 2,
    abruzzese: 4,
    abbruzzese: 4,
    aborigine: 5,
    acreage: 3,
    adame: 3,
    adieu: 2,
    adobe: 3,
    anemone: 4,
    aphrodite: 4,
    apache: 3,
    apostrophe: 4,
    ariadne: 4,
    cafe: 2,
    calliope: 4,
    catastrophe: 4,
    chloe: 2,
    chile: 2,
    circe: 2,
    coyote: 3,
    epitome: 4,
    forever: 3,
    gethsemane: 4,
    guacamole: 4,
    hyperbole: 4,
    jesse: 2,
    jukebox: 2,
    karate: 3,
    machete: 3,
    maybe: 2,
    people: 2,
    recipe: 3,
    sesame: 3,
    shoreline: 2,
    simile: 3,
    syncope: 3,
    tamale: 3,
    yosemite: 4,
    daphne: 2,
    eurydice: 4,
    euterpe: 3,
    hermione: 4,
    persephone: 4,
    penelope: 4,
    phoebe: 2,
    zoe: 2,
  })
);

const SILENT_ENDING_PATTERNS = [
  /cia(l|$)/,
  /tia/,
  /cius/,
  /cious/,
  /[^aeiou]giu/,
  /[aeiouy][^aeiouy]ion/,
  /iou/,
  /sia$/,
  /eous$/,
  /[oa]gue$/,
  /.[^aeiuoycgltdb]{2,}ed$/,
  /.ely$/,
  /^jua/,
  /uai/,
  /eau/,
  /[aeiouy](b|c|ch|d|dg|f|g|gh|gn|k|l|ll|lv|m|mm|n|nc|ng|nn|p|r|rc|rn|rs|rv|s|sc|sk|sl|squ|ss|st|t|th|v|y|z)e$/,
  /[aeiouy](b|c|ch|dg|f|g|gh|gn|k|l|lch|ll|lv|m|mm|n|nc|ng|nch|nn|p|r|rc|rn|rs|rv|s|sc|sk|sl|squ|ss|th|v|y|z)ed$/,
  /[aeiouy](b|ch|d|f|gh|gn|k|l|lch|ll|lv|m|mm|n|nch|nn|p|r|rn|rs|rv|s|sc|sk|sl|squ|ss|st|t|th|v|y)es$/,
  /^busi$/,
];

const EXTRA_SYLLABLE_PATTERNS = [
  /([^s]|^)ia/,
  /riet/,
  /dien/,
  /iu/,
  /io/,
  /eo($|[b-df-hj-np-tv-z])/,
  /ii/,
  /[ou]a$/,
  /[aeiouym]bl$/,
  /[aeiou]{3}/,
  /[aeiou]y[aeiou]/,
  /^mc/,
  /ism$/,
  /asm$/,
  /thm$/,
  /([^aeiouy])\1l$/,
  /[^l]lien/,
  /^coa[dglx]./,
  /[^gq]ua[^auieo]/,
  /dnt$/,
  /uity$/,
  /[^aeiouy]ie(r|st|t)$/,
  /eings?$/,
  /[aeiouy]sh?e[rsd]$/,
  /iell/,
  /dea$/,
  /real/,
  /[^aeiou]y[ae]/,
  /gean$/,
  /uen/,
];

const AFFIX_PATTERNS = [
  /^un/,
  /^fore/,
  /^ware/,
  /^none?/,
  /^out/,
  /^post/,
  /^sub/,
  /^pre/,
  /^pro/,
  /^dis/,
  /^side/,
  /ly$/,
  /less$/,
  /some$/,
  /ful$/,
  /ers?$/,
  /ness$/,
  /cians?$/,
  /ments?$/,
  /ettes?$/,
  /villes?$/,
  /ships?$/,
  /sides?$/,
  /ports?$/,
  /shires?$/,
  /tion(ed)?$/,
];

const DOUBLE_SYLLABLE_AFFIX_PATTERNS = [
  /^above/,
  /^ant[ie]/,
  /^counter/,
  /^hyper/,
  /^afore/,
  /^agri/,
  /^in[ft]ra/,
  /^inter/,
  /^over/,
  /^semi/,
  /^ultra/,
  /^under/,
  /^extra/,
  /^dia/,
  /^micro/,
  /^mega/,
  /^kilo/,
  /^pico/,
  /^nano/,
  /^macro/,
  /berry$/,
  /woman$/,
  /women$/,
];

const TRIPLE_SYLLABLE_AFFIX_PATTERNS = [/ology$/, /ologist$/, /onomy$/, /onomist$/];

function roundToTenth(value: number): number {
  return Math.round(10 * value) / 10;
}

function normalizeText(input: string): string {
  let text = input;
  for (const tag of TEXT_BLOCK_END_TAGS) {
    text = text.replace(`</${tag}>`, ".");
  }
  text = text
    .replace(/<[^>]+>/g, "")
    .replace(/[,:;()/&+]|--/g, " ")
    .replace(/[.!?]/g, ".")
    .replace(/^\s+/, "")
    .replace(/[.]?(\w+)[.]?(\w+)@(\w+)[.](\w+)[.]?/g, "$1$2@$3$4")
    .replace(/[ ]*(\n|\r\n|\r)[ ]*/g, ".")
    .replace(/(\.)\.+/g, ".")
    .replace(/[ ]*(\.)/g, ". ")
    .replace(/\s+/g, " ")
    .replace(/\s+$/, "");
  return text.endsWith(".") ? text : `${text}.`;
}

function countSyllables(rawWord: string): number {
  let word = rawWord.toLowerCase().replace(/[^a-z]/g, "");
  const knownSyllables = KNOWN_WORD_SYLLABLES.get(word);
  if (knownSyllables !== undefined) {
    return knownSyllables;
  }

  let affixCount = 0;
  for (const pattern of AFFIX_PATTERNS) {
    if (pattern.test(word)) {
      word = word.replace(pattern, "");
      affixCount++;
    }
  }

  let doubleAffixCount = 0;
  for (const pattern of DOUBLE_SYLLABLE_AFFIX_PATTERNS) {
    if (pattern.test(word)) {
      word = word.replace(pattern, "");
      doubleAffixCount++;
    }
  }

  let tripleAffixCount = 0;
  for (const pattern of TRIPLE_SYLLABLE_AFFIX_PATTERNS) {
    if (pattern.test(word)) {
      word = word.replace(pattern, "");
      tripleAffixCount++;
    }
  }

  const vowelGroupCount = word.split(/[^aeiouy]+/i).filter((group) => group.replace(/\s+/gi, "").length > 0).length;

  let syllables = vowelGroupCount + affixCount + 2 * doubleAffixCount + 3 * tripleAffixCount;
  for (const pattern of SILENT_ENDING_PATTERNS) {
    if (pattern.test(word)) {
      syllables--;
    }
  }
  for (const pattern of EXTRA_SYLLABLE_PATTERNS) {
    if (pattern.test(word)) {
      syllables++;
    }
  }
  return syllables || 1;
}

// Like the site, every step normalizes its own input and the steps call each other. normalizeText is not idempotent,
// so the formulas see text normalized up to three times; normalizing once up front gives different scores.
function sentenceCountOf(input: string): number {
  return normalizeText(input).replace(/[^.!?]/g, "").length || 1;
}

function wordCountOf(input: string): number {
  const text = normalizeText(input);
  const words = text
    .replace(/[‘’]/g, "'")
    .replace(WORD_NOISE_PATTERN, " ")
    .replace(WORD_NOISE_PATTERN, " ")
    .replace(/^['-\s]|['-\s]$/g, "")
    .toLowerCase()
    .split(" ");
  const hasAlphanumeric = /[a-zA-Z\d]/m.test(text);
  return (hasAlphanumeric ? words.length : 0) || 1;
}

function syllableCountOf(input: string): number {
  return normalizeText(input)
    .split(/\s+/)
    .reduce((total, word) => total + countSyllables(word), 0);
}

function averageWordsPerSentenceOf(input: string): number {
  const text = normalizeText(input);
  return roundToTenth(wordCountOf(text) / sentenceCountOf(text));
}

function averageSyllablesPerWordOf(input: string): number {
  const text = normalizeText(input);
  const syllables = text.split(/\s+/).reduce((total, word) => total + countSyllables(word), 0);
  return roundToTenth((syllables || 1) / (wordCountOf(text) || 1));
}

function easeScoreOf(input: string): number {
  const text = normalizeText(input);
  const score = roundToTenth(
    206.835 - 1.015 * averageWordsPerSentenceOf(text) - 84.6 * averageSyllablesPerWordOf(text)
  );
  return Math.min(100, Math.max(0, score));
}

function gradeLevelOf(input: string): number {
  const text = normalizeText(input);
  const grade = roundToTenth(0.39 * averageWordsPerSentenceOf(text) + 11.8 * averageSyllablesPerWordOf(text) - 15.59);
  return Math.max(0, grade);
}

export class FleschKincaid {
  constructor(private readonly text: string) {}

  get sentenceCount(): number {
    return sentenceCountOf(this.text);
  }

  get wordCount(): number {
    return wordCountOf(this.text);
  }

  get syllableCount(): number {
    return syllableCountOf(this.text);
  }

  get averageWordsPerSentence(): number {
    return averageWordsPerSentenceOf(this.text);
  }

  get averageSyllablesPerWord(): number {
    return averageSyllablesPerWordOf(this.text);
  }

  get easeScore(): number {
    return easeScoreOf(this.text);
  }

  get gradeLevel(): number {
    return gradeLevelOf(this.text);
  }

  get readingLevel(): ReadingLevel {
    const score = this.easeScore;
    const boundaryMatches = READING_LEVELS.filter((level) => score >= level.min && score <= level.max);
    const { schoolLevel, notes, id } = boundaryMatches[boundaryMatches.length - 1];
    return { schoolLevel, notes, id };
  }
}
